/**
 * cycle-window.js — 課金サイクル窓の単一ソース（ダッシュボード / 予約検証 共通）
 *
 * 背景:
 *  - Recharge API バージョン 2021-01 は timestamp を **ストアTZ(America/Los_Angeles)
 *    のローカル naive 文字列**（offset 指定子なし, 例 "2026-05-12T03:05:37"）で返す。
 *  - 旧コードは `new Date(naive)` でこれを **閲覧者ブラウザTZ** として誤パースし、
 *    さらに convertToTimeZone(toLocaleString→new Date) ハックと、UTC getter と
 *    local getter の混在で、月窓境界が閲覧者TZ依存で最大数時間ズレていた
 *    （境界±数時間で±1誤差）。
 *
 * 方針:
 *  - naive 文字列は常に明示的に STORE_TZ の壁時計として解釈し、**絶対 epoch(ms)** に
 *    変換する（閲覧者ブラウザTZに完全非依存・DST安全）。
 *  - サイクル窓 [startMs, endMs) を実課金日基準で対称に確定し、ダッシュボードと
 *    予約検証の両方が同一窓・同一カウントを使う。
 *
 * 仕様（確定済）: 「予約枠の消化数」= 予約作成時点(created_at)で1枠消費。
 *  status==="Cancelled" のみ除外し、Booking(未来)/Finished(受講済) は算入。
 *  created_at ∈ [cycleStart, cycleEnd) のものを当サイクルの消化としてカウント。
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }
  if (typeof window !== "undefined") {
    window.CycleWindow = api;
    // トライアル種別判定はグローバル直公開（getBasePlanLimit 等と同じ呼び出し規約）。
    // 既存定義があれば尊重する（先勝ち）。
    if (typeof window.getTrialInfo !== "function") {
      window.getTrialInfo = api.getTrialInfo;
    }
  }
})(this, function () {
  "use strict";

  var STORE_TZ = "America/Los_Angeles";

  /** 指定瞬間(utcMs)を tz の壁時計に分解した数値フィールドを返す */
  function partsInTz(utcMs, tz) {
    var dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    var o = {};
    dtf.formatToParts(new Date(utcMs)).forEach(function (p) {
      if (p.type !== "literal") o[p.type] = Number(p.value);
    });
    // Intl は深夜を "24" と返す実装があるため正規化
    if (o.hour === 24) o.hour = 0;
    return o;
  }

  /** 瞬間(utcMs)における tz のオフセット(ms) = (tzの壁時計) - utc */
  function tzOffsetMs(utcMs, tz) {
    var p = partsInTz(utcMs, tz);
    var asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUTC - utcMs;
  }

  /**
   * offset 指定子なしの naive 文字列を tz の壁時計として解釈し絶対 epoch(ms) を返す。
   * "YYYY-MM-DDTHH:MM:SS" / "YYYY-MM-DD HH:MM:SS" / 秒省略 を許容。
   * DST 切替を跨いでも収束する 2 パス補正。
   */
  function epochFromZonedNaive(naive, tz) {
    var m = String(naive)
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return NaN;
    var Y = +m[1],
      Mo = +m[2] - 1,
      D = +m[3],
      H = +m[4],
      Mi = +m[5],
      S = m[6] ? +m[6] : 0;
    var base = Date.UTC(Y, Mo, D, H, Mi, S);
    var ts = base;
    for (var i = 0; i < 2; i++) {
      ts = base - tzOffsetMs(ts, tz);
    }
    return ts;
  }

  /**
   * 絶対 epoch(ms) を「tz の壁時計で n ヶ月加減」した epoch(ms) を返す。
   * 月末日は加減先の月の日数にクランプ（例: 1/31 +1ヶ月 → 2/28）。
   */
  function addMonthsInTz(ms, n, tz) {
    var p = partsInTz(ms, tz);
    var total = p.month - 1 + n;
    var y = p.year + Math.floor(total / 12);
    var mo = ((total % 12) + 12) % 12;
    var daysInMo = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
    var d = Math.min(p.day, daysInMo);
    var pad = function (x) {
      return String(x).padStart(2, "0");
    };
    var naive =
      y +
      "-" +
      pad(mo + 1) +
      "-" +
      pad(d) +
      "T" +
      pad(p.hour) +
      ":" +
      pad(p.minute) +
      ":" +
      pad(p.second);
    return epochFromZonedNaive(naive, tz);
  }

  /**
   * App Proxy(/apps/booking-app/recharge/subscriptions) が返す日付値を
   * 絶対 epoch(ms) に変換する。実形式は 3 種（2026-05 実機検証済）:
   *   1. offset/Z 付き ISO（例 "2026-05-19T00:47:35+00:00"）= 絶対時刻
   *      → offset が明示されているので Date.parse で一意（TZ非依存）
   *   2. 日付のみ "YYYY-MM-DD"（例 next_charge_scheduled_at "2026-07-07"）
   *      → Recharge の課金日は store-local。STORE_TZ の深夜 0 時として解釈
   *   3. offset 無し naive datetime（旧 Recharge 2021-01 直 API 形式）
   *      → STORE_TZ の壁時計として解釈（保険）
   * 旧実装は 1 を naive 誤解釈し、2 は正規表現不一致で NaN を返していた
   * （NaN により窓上限が壊れ消化数が常に 0 になる不具合の原因）。
   */
  function toEpochMs(value, tz) {
    if (value == null) return NaN;
    var s = String(value).trim();
    if (!s) return NaN;
    if (/[zZ]$/.test(s) || /[+\-]\d{2}:?\d{2}$/.test(s)) {
      var t = Date.parse(s);
      return isNaN(t) ? NaN : t;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return epochFromZonedNaive(s + "T00:00:00", tz || STORE_TZ);
    }
    return epochFromZonedNaive(s, tz || STORE_TZ);
  }

  /**
   * 現サイクル窓 [startMs, endMs)。
   *  - 下限 = subscription.updated_at（直近課金/更新。offset付きISOなら絶対時刻）
   *  - 上限 = subscription.next_charge_scheduled_at（次回実課金日。日付のみ＝
   *           STORE_TZ 深夜）。無い場合のみ updated_at+1ヶ月でフォールバック
   * 既知の限界: updated_at は課金以外の更新でも動くため、厳密には直近 SUCCESS
   *  charge.scheduled_at が望ましい（proxy 拡張は別タスク）。
   */
  function getCurrentCycleWindow(subscription, tz) {
    tz = tz || STORE_TZ;
    var startMs = toEpochMs(subscription.updated_at, tz);
    var endMs = subscription.next_charge_scheduled_at
      ? toEpochMs(subscription.next_charge_scheduled_at, tz)
      : addMonthsInTz(startMs, 1, tz);
    return { startMs: startMs, endMs: endMs };
  }

  /**
   * 月セレクタ index 用の窓。i=0=現サイクル（next_charge 基準）。
   * i>=1 は現サイクル開始を STORE_TZ 壁時計で i ヶ月戻した窓。
   */
  function getCycleWindowForIndex(subscription, i, tz) {
    tz = tz || STORE_TZ;
    var cur = getCurrentCycleWindow(subscription, tz);
    if (!i) return cur;
    return {
      startMs: addMonthsInTz(cur.startMs, -i, tz),
      endMs: addMonthsInTz(cur.startMs, -i + 1, tz),
    };
  }

  /**
   * 当サイクルで消化した枠（仕様: 予約時点消化 / Cancelled のみ除外 /
   * created_at ∈ [startMs, endMs)）。
   */
  function countConsumedSlots(records, window) {
    var s = window.startMs,
      e = window.endMs;
    return (records || []).filter(function (r) {
      if (!r || r.status === "Cancelled") return false;
      var c = Number(r.created_at) * 1000; // metaobject created_at は Unix 秒（絶対時刻）
      if (!isFinite(c)) return false;
      return c >= s && c < e;
    });
  }

  /**
   * 顧客タグからトライアル種別と制限仕様を判定する（プラン表示・予約検証の共有ソース）。
   *  - "Trial_<ISO8601 UTC>": 7-Day Trial Pass（$10）購入時に Shopify Flow が付与する主入口
   *      （リファラル REF-7DAY-TRIAL で $0 になる場合も同じタグ）。3回・有効期限7日・同時予約2。
   *  - "Try_for_free_<ISO8601 UTC>": 2026-06〜09 の Try for Free ファネル（business.1om1.net /try）が
   *      付与していたレガシー。1回・有効期限なし・同時予約1。新規付与は無い。
   * 複数タグが併存する場合は「開始日時が最新のタグ」を採用する（後から購入した権利が勝つ。
   * 同時刻なら Trial_ を優先）。日時を解釈できないタグは無視する。
   * なお Try_for_free_ は "Trial_" を部分一致で含まないため、prefix 一致で確実に区別できる。
   * kind 名（referral_7day / try_for_free）は既存の呼び出し側との互換のため据え置き。
   * @param {string[]} tags customer.tags 配列
   * @returns {{kind:string, startDate:Date, baseLimit:number, maxSimultaneous:number, windowDays:(number|null)}|null}
   */
  function getTrialInfo(tags) {
    if (!Array.isArray(tags)) return null;
    var SPECS = [
      { prefix: "Trial_", kind: "referral_7day", baseLimit: 3, maxSimultaneous: 2, windowDays: 7 },
      { prefix: "Try_for_free_", kind: "try_for_free", baseLimit: 1, maxSimultaneous: 1, windowDays: null },
    ];
    var best = null;
    tags.forEach(function (t) {
      if (typeof t !== "string") return;
      SPECS.forEach(function (spec) {
        if (t.indexOf(spec.prefix) !== 0) return;
        var d = new Date(t.slice(spec.prefix.length));
        var ms = d.getTime();
        if (isNaN(ms)) return;
        var newer = !best || ms > best.ms;
        var tieToPass = best && ms === best.ms && spec.kind === "referral_7day" && best.spec.kind !== "referral_7day";
        if (newer || tieToPass) best = { ms: ms, spec: spec, date: d };
      });
    });
    if (!best) return null;
    return {
      kind: best.spec.kind,
      startDate: best.date,
      baseLimit: best.spec.baseLimit,
      maxSimultaneous: best.spec.maxSimultaneous,
      windowDays: best.spec.windowDays,
    };
  }

  return {
    STORE_TZ: STORE_TZ,
    partsInTz: partsInTz,
    tzOffsetMs: tzOffsetMs,
    epochFromZonedNaive: epochFromZonedNaive,
    toEpochMs: toEpochMs,
    addMonthsInTz: addMonthsInTz,
    getCurrentCycleWindow: getCurrentCycleWindow,
    getCycleWindowForIndex: getCycleWindowForIndex,
    countConsumedSlots: countConsumedSlots,
    getTrialInfo: getTrialInfo,
  };
});
